/**
 * Flatten the orchestrator's result object (plus identity + checklist) into the row
 * shape that matches `public.test_results` in supabase/schema.sql.
 */
export function buildTestResultRow(result) {
  const m = result.metrics || {};
  const v = result.verdicts || {};
  const machine = result.machine || {};
  const env = result.environment || {};
  const id = result.identity || {};
  const ck = result.checklist || {};

  let durationMs = null;
  if (result.started_at && result.completed_at) {
    const start = new Date(result.started_at).getTime();
    const end = new Date(result.completed_at).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      durationMs = end - start;
    }
  }

  return {
    attempt_id: result.attempt_id ?? null,
    attempt_status: result.attempt_status ?? 'completed',
    failure_reason: result.failure_reason ?? null,
    last_phase: result.last_phase ?? null,

    full_name: id.full_name ?? null,
    email: id.email ?? null,

    overall_result: result.overall_result ?? null,

    download_mbps: m.downloadMbps ?? null,
    download_verdict: v.downloadMbps ?? null,
    download_timed_out: Boolean(m.downloadTimedOut),
    upload_mbps: m.uploadMbps ?? null,
    upload_verdict: v.uploadMbps ?? null,
    upload_timed_out: Boolean(m.uploadTimedOut),
    capacity_download_mbps: m.capacityDownloadMbps ?? null,
    capacity_download_verdict: v.capacityDownloadMbps ?? null,
    capacity_download_completed: Boolean(m.capacityDownloadCompleted),
    capacity_upload_mbps: m.capacityUploadMbps ?? null,
    capacity_upload_verdict: v.capacityUploadMbps ?? null,
    capacity_upload_completed: Boolean(m.capacityUploadCompleted),
    packet_loss_pct: m.packetLossPct ?? null,
    packet_loss_verdict: v.packetLossPct ?? null,
    jitter_ms: m.jitterMs ?? null,
    jitter_verdict: v.jitterMs ?? null,
    rtt_ms: m.rttMs ?? null,
    rtt_verdict: v.rttMs ?? null,
    bufferbloat_increase_ms: m.bufferbloatIncreaseMs ?? null,
    bufferbloat_verdict: v.bufferbloatIncreaseMs ?? null,
    websocket_p95_ms: m.websocketP95Ms ?? null,
    websocket_verdict: v.websocketP95Ms ?? null,
    mos_score: m.mosScore ?? null,
    mos_verdict: v.mosScore ?? null,

    cpu_ops_per_sec: machine.cpuOpsPerSec ?? null,
    cpu_verdict: machine.cpuVerdict ?? null,
    device_memory_gb: machine.deviceMemoryGb ?? null,
    cpu_cores: machine.cpuCores ?? null,
    browser_version: machine.browserVersion ?? null,
    browser_age_months: machine.browserAgeMonths ?? null,
    hardware_acceleration_enabled: machine.hardwareAccelerationEnabled ?? null,
    webrtc_supported: machine.webrtcSupported ?? null,
    webcodecs_supported: machine.webcodecsSupported ?? null,
    webgl2_supported: machine.webgl2Supported ?? null,
    hw_video_codecs: machine.hwVideoCodecs ?? null,
    hw_audio_codecs: machine.hwAudioCodecs ?? null,
    incognito_detected: machine.incognitoDetected ?? null,
    on_battery: machine.onBattery ?? null,
    memory_headroom_bytes: machine.memoryHeadroomBytes ?? null,
    clutter_score: machine.clutterScore ?? null,
    machine_verdict: machine.machineVerdict ?? null,

    detected_country: env.detected_country ?? null,
    detected_city: env.detected_city ?? null,
    detected_isp: env.detected_isp ?? null,
    public_ip: env.public_ip ?? null,
    user_agent: env.user_agent ?? null,
    browser: env.browser ?? null,
    os: env.os ?? null,
    connection_type: env.connection_type ?? null,
    via_cloudflare_tunnel: Boolean(env.via_cloudflare_tunnel),

    confirmed_wired: ck.confirmed_wired ?? null,
    confirmed_apps_closed: ck.confirmed_apps_closed ?? null,
    confirmed_work_hours: ck.confirmed_work_hours ?? null,

    raw_metrics: result.raw_metrics ?? null,
    test_duration_ms: durationMs
  };
}

export function buildAttemptEventRow({
  attemptId,
  eventType,
  phase = null,
  elapsedMs = null,
  identity = {},
  detail = null
}) {
  return {
    attempt_id: attemptId,
    event_type: eventType,
    phase,
    elapsed_ms: Number.isFinite(elapsedMs) ? Math.max(0, Math.round(elapsedMs)) : null,
    full_name: identity.full_name ?? null,
    email: identity.email ?? null,
    detail
  };
}

export function buildFailedTestResult({
  attemptId,
  identity,
  checklist,
  phase,
  error,
  startedAt,
  completedAt = new Date().toISOString()
}) {
  const failureReason = error?.message || String(error || 'Unknown test failure');
  const noDataVerdicts = {
    downloadMbps: 'no_data',
    uploadMbps: 'no_data',
    capacityDownloadMbps: 'no_data',
    capacityUploadMbps: 'no_data',
    packetLossPct: 'no_data',
    jitterMs: 'no_data',
    rttMs: 'no_data',
    bufferbloatIncreaseMs: 'no_data',
    websocketP95Ms: 'no_data',
    websocketReconnects: 'no_data',
    mosScore: 'no_data'
  };

  return {
    attempt_id: attemptId,
    attempt_status: 'failed',
    failure_reason: failureReason,
    last_phase: phase ?? null,
    started_at: startedAt,
    completed_at: completedAt,
    overall_result: 'inconclusive',
    metrics: {
      downloadMbps: null,
      uploadMbps: null,
      capacityDownloadMbps: null,
      capacityUploadMbps: null,
      capacityDownloadCompleted: false,
      capacityUploadCompleted: false,
      downloadTimedOut: false,
      uploadTimedOut: false,
      packetLossPct: null,
      jitterMs: null,
      rttMs: null,
      bufferbloatIncreaseMs: null,
      websocketP95Ms: null,
      websocketReconnects: null,
      mosScore: null
    },
    verdicts: noDataVerdicts,
    machine: null,
    environment: {},
    raw_metrics: {
      failure: {
        phase: phase ?? null,
        error: failureReason
      }
    },
    identity,
    checklist
  };
}

/**
 * Persist a completed test result to supabase. Caller passes the supabase client
 * (which may be null when env vars are not configured) and the result object from
 * the orchestrator (already augmented with identity + checklist).
 *
 * Returns: { ok: true } | { ok: false, error: string }
 */
export async function saveTestResult(supabase, result) {
  if (!supabase) {
    return {
      ok: false,
      error: 'Persistence not configured: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is missing.'
    };
  }
  const row = buildTestResultRow(result);
  try {
    const { error } = await supabase.from('test_results').insert(row);
    if (error) {
      return { ok: false, error: error.message || String(error) };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

export async function recordAttemptEvent(supabase, event) {
  if (!supabase) {
    return {
      ok: false,
      error: 'Persistence not configured: VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is missing.'
    };
  }
  const row = buildAttemptEventRow(event);
  try {
    const { error } = await supabase.from('test_attempt_events').insert(row);
    if (error) {
      return { ok: false, error: error.message || String(error) };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}
